import {
  CartLinesUpdateEvent,
  CartErrorEvent
} from '@shopify/events';


class CartPackControls extends HTMLElement {

  connectedCallback() {
    this.addEventListener(
      'click',
      this.handleClick
    );
  }


  disconnectedCallback() {
    this.removeEventListener(
      'click',
      this.handleClick
    );
  }


  handleClick = async (event) => {

    const button = event.target.closest(
      '[data-pack-action]'
    );

    if (!button) return;


    const action =
      button.dataset.packAction;

    const currentQuantity =
      Number(this.dataset.quantity || 1);


    let newQuantity = currentQuantity;


    if (action === 'increase') {
      newQuantity = currentQuantity + 1;
    }


    if (action === 'decrease') {

      if (currentQuantity <= 1) {
        return;
      }

      newQuantity = currentQuantity - 1;
    }


    if (action === 'remove') {
      newQuantity = 0;
    }


    await this.updatePack(
      newQuantity,
      action
    );
  };


  updatePack = async (
    quantity,
    action
  ) => {

    /*
     * GET ALL REAL SHOPIFY CART LINE KEYS
     * BELONGING TO THIS PACK.
     */

    const lineInputs =
      this.querySelectorAll(
        '.cart-pack-controls__line'
      );


    const lineKeys =
      Array.from(lineInputs)
        .map((input) => input.value)
        .filter(Boolean);


    if (!lineKeys.length) return;


    /*
     * BUILD THE MULTI-LINE UPDATE.
     *
     * All products belonging to the pack
     * always receive the same pack quantity.
     */

    const updates = {};


    lineKeys.forEach((key) => {

      updates[key] =
        quantity;

    });


    /*
     * FIND ALL ATELIER CART COMPONENTS.
     *
     * Usually:
     *
     * - cart drawer
     * - main cart page
     */

    const cartItemsComponents =
      document.querySelectorAll(
        'cart-items-component'
      );


    /*
     * BUILD THE SAME SECTION LIST
     * ATELIER USES INTERNALLY.
     */

    const sectionsToUpdate =
      new Set();


    cartItemsComponents.forEach(
      (component) => {

        if (
          component instanceof HTMLElement &&
          component.dataset.sectionId
        ) {

          sectionsToUpdate.add(
            component.dataset.sectionId
          );

        }

      }
    );


    /*
     * CREATE ATELIER'S DEFERRED
     * CART UPDATE PROMISE.
     */

    const deferredUpdatePromise =
      CartLinesUpdateEvent.createPromise();


    /*
     * DISPATCH BEFORE THE REQUEST.
     *
     * Every cart-items-component listens for this.
     *
     * Because this event originates from our
     * cart-pack-controls element rather than the
     * cart-items-component itself, Atelier treats
     * this as an external cart update.
     */

    this.dispatchEvent(
      new CartLinesUpdateEvent({

        action:
          quantity === 0
            ? 'remove'
            : 'update',

        context:
          'cart',

        lines:
          lineKeys.map((key) => ({
            id: key,
            quantity
          })),

        promise:
          deferredUpdatePromise.promise

      })
    );


    /*
     * BUSY STATE
     */

    this.setAttribute(
      'aria-busy',
      'true'
    );


    try {

      /*
       * UPDATE ALL PACK LINES
       * IN ONE SHOPIFY REQUEST.
       *
       * IMPORTANT:
       *
       * Match Atelier's own Section Rendering
       * API request format.
       */

      const response =
        await fetch(
          Theme.routes.cart_update_url ||
          `${window.Shopify.routes.root}cart/update.js`,
          {

            method:
              'POST',

            headers: {
              'Content-Type':
                'application/json',

              Accept:
                'application/json'
            },

            credentials:
              'same-origin',

            body:
              JSON.stringify({

                updates,

                sections:
                  Array
                    .from(sectionsToUpdate)
                    .join(','),

                sections_url:
                  window.location.pathname

              })

          }
        );


      const result =
        await response.json();


      /*
       * SHOPIFY ERROR
       */

      if (
        !response.ok ||
        result.status ||
        result.errors
      ) {

        const errorMessage =
          result.description ||
          result.message ||
          result.errors ||
          'Impossible de mettre à jour la parure.';


        deferredUpdatePromise.reject(
          new Error(errorMessage)
        );


        this.dispatchEvent(
          new CartErrorEvent({

            error:
              errorMessage,

            code:
              'INVALID',

            detail: {

              description:
                result.description,

              errors:
                result.errors

            }

          })
        );


        return;
      }


      /*
       * CALCULATE THE NEW CART ITEM COUNT.
       *
       * Shopify's /cart/update.js response already
       * contains the complete updated cart.
       */

      const itemCount =
        Array.isArray(result.items)
          ? result.items.reduce(
              (total, item) =>
                total +
                Number(item.quantity || 0),
              0
            )
          : 0;


      /*
       * RESOLVE THE EVENT.
       *
       * THIS IS THE IMPORTANT PART.
       *
       * Atelier's cart-items-component receives
       * this result and automatically calls:
       *
       * morphSection(...)
       *
       * for both the drawer and main cart.
       */

      deferredUpdatePromise.resolve({

        cart:
          CartLinesUpdateEvent
            .createCartFromAjaxResponse(
              result
            ),

        detail: {

          sections:
            result.sections,

          items:
            result.items,

          itemCount,

          source:
            'cart-pack',

          sourceId:
            this.dataset.packId || '',

          didError:
            false

        }

      });


    } catch (error) {

      console.error(
        '[cart-pack]',
        error
      );


      /*
       * REJECT ATELIER'S EVENT PROMISE.
       */

      deferredUpdatePromise.reject(
        error
      );


      this.dispatchEvent(
        new CartErrorEvent({

          error:
            error?.message ||
            'Impossible de mettre à jour la parure.',

          code:
            'SERVICE_UNAVAILABLE'

        })
      );


    } finally {

      /*
       * The successful morph may already
       * have replaced this element.
       */

      if (this.isConnected) {

        this.removeAttribute(
          'aria-busy'
        );

      }

    }

  };

}


if (
  !customElements.get(
    'cart-pack-controls'
  )
) {

  customElements.define(
    'cart-pack-controls',
    CartPackControls
  );

}